// NX-6284: Ireland "8% of worked hours (irregular)" holiday — the PRHP hours
// limit a timesheet form shows, read from the backend for the timesheet's own
// pay period so it is the limit the backend enforces on save.
import type { AppRateFormItem } from '@nxsys/components/AppRateForm';
import { HP_CODES } from '@nxsys/constants/AppConst';
import { useGetIrregularHolidayTimesheetView } from '@nxsys/hooks/api';
import type { IrregularHolidayTimesheetViewParams } from '@nxsys/types/api';
import { useEffect, useMemo } from 'react';

// Hours worked on the form's non-holiday lines, for the leaver suggestion: a
// final period's own work accrues holiday that is owed with the rest. An
// HOURLY-unit rate counts its units as hours; any other unit converts via
// hoursPerUnit; a rate that can't convert, and statutory lines, add nothing.
export const irregularHolidayWorkedHours = (
  rates: AppRateFormItem[] | undefined,
): number =>
  (rates ?? []).reduce((sum, r) => {
    const code = r?.rateCode?.value ?? r?.rateCode?.fullData?.code;
    if (!code || HP_CODES.includes(code)) return sum;
    const rateData = r?.rateCode?.fullData;
    if (rateData?.isStatutory) return sum;
    const units = Number(r?.units) || 0;
    if (units <= 0) return sum;
    if (rateData?.units === 'HOURLY') return sum + units;
    const hoursPerUnit = Number(rateData?.hoursPerUnit);
    return hoursPerUnit > 0 ? sum + units * hoursPerUnit : sum;
  }, 0);

/**
 * Fetches the timesheet view for `subcontractor` and the timesheet's period
 * (`params`: the new timesheet's tax period, or the edited timesheet's id),
 * and returns the subcontractor with `irregularHoliday` merged in for
 * AppRateForm when the method applies. `params` changes refetch; on a P45
 * timesheet (`debounce`) they change with every keystroke on the rate lines,
 * so the call waits for typing to settle.
 */
export const useIrregularHolidayTimesheetLimit = <
  T extends { id?: string } | null | undefined,
>({
  subcontractor,
  enabled,
  params,
  debounce = false,
}: {
  subcontractor: T;
  enabled: boolean;
  params: IrregularHolidayTimesheetViewParams;
  debounce?: boolean;
}) => {
  const { run, value: view } = useGetIrregularHolidayTimesheetView();
  const paramsKey = JSON.stringify(params);

  useEffect(() => {
    if (!subcontractor?.id || !enabled) return;
    if (!debounce) {
      run(subcontractor.id, params);
      return;
    }
    const timer = setTimeout(
      () => run(subcontractor.id as string, params),
      400,
    );
    return () => clearTimeout(timer);
  }, [subcontractor?.id, enabled, paramsKey, debounce]);

  const applies = !!view?.applies;
  const subcontractorForRateForm = useMemo(() => {
    if (!subcontractor || !applies || !view) return subcontractor;
    return {
      ...subcontractor,
      irregularHoliday: {
        remainingHours: view.remainingHours,
        // The backend picks the cap: on a final (P45) payroll it is the
        // leaver suggestion, since the period's own accrual is owed too.
        maxHolidayHours: view.maxHolidayHours,
        holidayPayRate: view.holidayPayRate,
        rateIsManual: view.rateIsManual,
      },
    };
  }, [subcontractor, applies, view]);

  return { view, subcontractorForRateForm };
};
